import { Task, TaskStatus } from '@lit/task';
import type { ReactiveController, ReactiveControllerHost, TemplateResult } from 'lit';

import {
    getNutrientBalanceSettings,
    type INutrientBalanceSettings,
} from './api_client/nutrient_balance.js';
import { valueOrThrow } from './api_result.js';
import type { INutrientBalanceChangedDetail } from './component_events.js';
import { loadFailed } from './error_copy.js';
import { cardSkeleton, liveRegion } from './skeletons.js';
import { TaskSection } from './task_body.js';
import { type IYearTarget, yearKeyOf } from './year_target.js';

/**
 * Decides before the year shell draws the area whether the Nährstoffvergleich shows its tabs or
 * the switch alone: a tab drawn while the Nährstoffbilanz is off would load its data for nothing,
 * and its address has to show the switch.
 *
 * The shell reads the settings for the open year while the area is open and takes those a leaf
 * reports after its own write (`fm-nutrient-balance-changed`), which bubbles out of the shell's
 * shadow root onto the shell itself.
 */
export class NutrientBalanceGate implements ReactiveController {

    private readonly host: ReactiveControllerHost & HTMLElement;

    private readonly target: () => IYearTarget | null;

    private readonly switchedOn: () => void;

    /** The settings a leaf last reported, and the year they belong to. */
    private reported: { identity: string;
        settings: INutrientBalanceSettings; } | null = null;

    private readonly task: Task<readonly [string, string], INutrientBalanceSettings | null>;

    private readonly section: TaskSection<readonly [string, string], INutrientBalanceSettings>;

    /**
     * @param host - The year shell.
     * @param target - The open year while the Nährstoffvergleich is open, otherwise null.
     * @param read - The read endpoint, the shell's seam.
     * @param switchedOn - Called when a leaf reports the Nährstoffbilanz switched on while the
     * area showed it off, which only the switch can.
     */
    public constructor (
        host: ReactiveControllerHost & HTMLElement,
        target: () => IYearTarget | null,
        read: () => typeof getNutrientBalanceSettings,
        switchedOn: () => void
    ) {
        this.host = host;
        this.target = target;
        this.switchedOn = switchedOn;
        this.task = new Task(host, {
            task: async ([
                farmId, fertilizerYearId,
            ], { signal }): Promise<INutrientBalanceSettings | null> => {

                // Reopening the area shows the read, not a report another write may have overtaken.
                if (farmId === '' || fertilizerYearId === '') {
                    this.reported = null;

                    return null;
                }

                return valueOrThrow(await read()(farmId, fertilizerYearId, {
                    signal,
                }));
            },
            args: (): readonly [string, string] => {
                const year = this.target();

                return [
                    year?.farmId ?? '',
                    year?.fertilizerYearId ?? '',
                ];
            },
        });
        this.section = new TaskSection(this.task, loadFailed('Die Einstellungen der Nährstoffbilanz', true), () => (
            this.identity()
        ));
        host.addController(this);
    }

    public hostConnected (): void {
        this.host.addEventListener('fm-nutrient-balance-changed', this.handleChanged);
    }

    public hostDisconnected (): void {
        this.host.removeEventListener('fm-nutrient-balance-changed', this.handleChanged);
    }

    /**
     * The open year as one string, the key of the held and the reported settings.
     * @returns The identity, empty while no year is open.
     */
    private identity (): string {
        const year = this.target();

        return year === null ? '' : yearKeyOf(year);
    }

    private readonly handleChanged = (event: CustomEvent<INutrientBalanceChangedDetail>): void => {
        const wasOff = this.switchedOff;

        this.reported = {
            identity: this.identity(),
            settings: event.detail.settings,
        };

        if (wasOff && event.detail.settings.nutrientBalanceEnabled) {
            this.switchedOn();
        }

        this.host.requestUpdate();
    };

    /**
     * Whether a completed read or a leaf's report shows the Nährstoffbilanz of the open year
     * switched off. False while unknown, so the page changes nothing before the area's body does.
     * @returns Whether it is switched off.
     */
    public get switchedOff (): boolean {
        const held = this.task.status === TaskStatus.COMPLETE ? this.task.value ?? null : null;
        const settings = this.current(held);

        return settings !== null && !settings.nutrientBalanceEnabled;
    }

    /**
     * Whether a leaf switched the Nährstoffbilanz of the open year off since the area was opened.
     * Only the „Einstellungen“ can, and they leave the screen, so the switch confirms instead.
     * @returns Whether a leaf switched it off.
     */
    public get switchedOffByLeaf (): boolean {
        const { reported } = this;

        return reported !== null && reported.identity === this.identity() && !reported.settings.nutrientBalanceEnabled;
    }

    /**
     * The settings that decide the area: a leaf's last report for this year, else the read's.
     * @param held - What the read answered for this year, or null.
     * @returns The settings, or null while there are none yet.
     */
    private current (held: INutrientBalanceSettings | null): INutrientBalanceSettings | null {
        const { reported } = this;

        return reported !== null && reported.identity === this.identity() ? reported.settings : held;
    }

    /**
     * The area's body under the read's load-error callout; a skeleton while the read is out.
     * @param body - The body, drawn from the settings.
     * @returns The section.
     */
    public render (body: (settings: INutrientBalanceSettings) => TemplateResult): TemplateResult {
        return this.section.render(held => {
            const settings = this.current(held);

            return settings === null ? liveRegion(cardSkeleton()) : body(settings);
        });
    }
}
